import { createFileRoute } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";
import { SiteNav } from "@/components/SiteNav";
import { SiteFooter } from "@/components/SiteFooter";
import { Hero } from "@/components/Hero";
import { Reveal } from "@/components/Reveal";
import { MailArt } from "@/components/ProductArt";

export const Route = createFileRoute("/kontakt")({
  head: () => ({
    meta: [
      { title: "Kontakt — Tarifvergleiche.com" },
      {
        name: "description",
        content:
          "Fragen, Feedback oder ein fehlendes Produkt? Schreib uns — wir antworten schnell.",
      },
      { property: "og:title", content: "Kontakt" },
      {
        property: "og:description",
        content:
          "Schreib uns zu Preisvergleichen, Feedback oder Produktwünschen.",
      },
    ],
  }),
  component: ContactPage,
});

const channels = [{ label: "Email", value: "jorek@impli.de" }];

function ContactPage() {
  const [sent, setSent] = useState(false);
  const [saved, setSaved] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });

  const updateField = (name: keyof typeof formData, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSending(true);
    setError(false);

    try {
      const res = await fetch("/api/formular", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(formData),
      });
      if (!res.ok) throw new Error("submission failed");
      const data: { ok: boolean; saved?: boolean } = await res.json();
      setSent(true);
      setSaved(Boolean(data.saved));
    } catch {
      setError(true);
    } finally {
      setSending(false);
    }
  };

  return (
    <main className="relative">
      <SiteNav />

      <Hero
        eyebrow="Get in touch"
        title={
          <>
            Sag <span className="text-primary">Hallo</span>.
          </>
        }
        subtitle="Eine kurze Nachricht reicht. Wir melden uns innerhalb von 24 Stunden zurück."
        decoration={
          <div className="flex h-full items-center justify-center">
            <div
              className="relative rounded-2xl border border-foreground/12 bg-card p-10 ticket-shadow"
              style={{ transform: "rotate(-3deg)" }}
            >
              <span className="punch-hole" />
              <MailArt className="h-48 w-48 text-foreground" tone="tag" />
            </div>
          </div>
        }
      />

      <section className="relative pb-32 px-6">
        <div className="mx-auto max-w-5xl grid md:grid-cols-[1fr_320px] gap-12">
          <Reveal>
            <form
              onSubmit={handleSubmit}
              className="relative bg-card border border-foreground/12 ticket-shadow rounded-2xl p-8 md:p-10 space-y-6"
            >
              <span className="punch-hole" />
              <div className="grid md:grid-cols-2 gap-6">
                <Field
                  label="Name"
                  name="name"
                  value={formData.name}
                  onChange={(val) => updateField("name", val)}
                />
                <Field
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={(val) => updateField("email", val)}
                />
              </div>
              <div>
                <label className="text-xs font-mono uppercase tracking-[0.18em] text-muted-foreground">
                  Worum geht's?
                </label>
                <textarea
                  required
                  name="message"
                  value={formData.message}
                  onChange={(e) => updateField("message", e.target.value)}
                  rows={6}
                  className="mt-2 w-full bg-transparent border-0 border-b-2 border-dotted border-foreground/25 focus:border-primary outline-none py-3 text-foreground placeholder:text-muted-foreground transition-colors resize-none"
                  placeholder="Frage, Feedback oder ein Produkt, das noch fehlt..."
                />
              </div>

              {sent && (
                <p className="text-sm font-medium text-accent">
                  Nachricht gesendet.
                  {saved && " Wir melden uns innerhalb von 24 Stunden."}
                </p>
              )}

              {error && (
                <p className="text-sm text-destructive">
                  Fehler. Bitte versuch es später erneut oder schreib uns direkt
                  per Email.
                </p>
              )}

              <button
                type="submit"
                className="w-full md:w-auto rounded-full border-2 border-foreground bg-foreground px-10 py-4 text-sm font-semibold text-background transition-colors hover:bg-primary hover:border-primary disabled:opacity-60"
                disabled={sending || sent}
              >
                {sent
                  ? "✓ Gesendet — danke!"
                  : sending
                    ? "Wird gesendet…"
                    : "Nachricht senden"}
              </button>
            </form>
          </Reveal>

          <Reveal delay={150}>
            <div className="space-y-8">
              <div>
                <div className="text-xs font-mono uppercase tracking-[0.22em] text-primary mb-4">
                  Direkt
                </div>
                <div className="space-y-4">
                  {channels.map((c) => (
                    <div
                      key={c.label}
                      className="border-b border-dotted border-foreground/25 pb-3"
                    >
                      <div className="text-xs font-mono uppercase tracking-[0.16em] text-muted-foreground">
                        {c.label}
                      </div>
                      <div className="font-mono text-sm mt-1 break-all text-foreground">
                        {c.value}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-2xl border border-foreground/12 bg-card p-6 ticket-shadow">
                <div className="flex items-center gap-2 mb-2">
                  <span className="h-2 w-2 rounded-full bg-accent" />
                  <span className="text-xs font-mono uppercase tracking-[0.16em] text-muted-foreground">
                    Erreichbar
                  </span>
                </div>
                <p className="text-sm text-foreground/80">
                  Wir antworten in der Regel innerhalb von 24 Stunden.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <SiteFooter />
    </main>
  );
}

function Field({
  label,
  name,
  type = "text",
  value,
  onChange,
}: {
  label: string;
  name: string;
  type?: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div>
      <label className="text-xs font-mono uppercase tracking-[0.18em] text-muted-foreground">
        {label}
      </label>
      <input
        required
        name={name}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-2 w-full bg-transparent border-0 border-b-2 border-dotted border-foreground/25 focus:border-primary outline-none py-3 text-foreground transition-colors"
      />
    </div>
  );
}
